import * as aes256 from "aes256";
import http from "@/http/default/axios";
import { firebase, firebaseConfig } from "@/firebase/firebaseConfig.js";
import Store from "@/store/store.js";
import localStorage from "@/db/aisea";
import moment from "moment";
import CryptoJS from "crypto-js";

const encryptKey = process.env.VUE_APP_ENCRYPT_KEY;
const db = localStorage.getInstance();

class AuthService {
  ADDON_IDS = {
    PORT_MASTER: "1",
    VESSEL_REPORT: "2",
    SECURE_DATAFLOW: "3",
    REGISTERED_TRAVERSER: "4",
    POLARIS: "5",
    ECO_SHIP: "6",
    CREW_TIER1: "7",
    CREW_TIER2: "8",
    CREW_TIER3: "9",
    DOCUMENT_MANAGER: "19",
    VESSEL_NOON_REPORT: "22",
    ENTERPRISE_HULL_INSURANCE: "23",
    AISEA_OPERATION: "25",
    CREW_PAYROLL: "27",
    FLOATING_UNIT: "28",
    RADAR_GRID: "30",
    FOR_OHMINATO: "34",
    SAFETY_DOCUMENT: "36",
  };

  async hasUserInfoInLocalDb() {
    let res = await db.auth.get(1);
    return res !== undefined && !!res.idToken;
  }

  async isExpiryToken() {
    let res = await db.auth.get(1);
    let from = moment(res.updated_at);
    let now = moment();
    let diff = now.diff(from, "minutes");
    return diff >= 60 ? true : false;
  }

  async getIdToken() {
    let res = await db.auth.get(1);
    return aes256.decrypt(encryptKey, res.idToken);
  }

  async getRefreshToken() {
    try {
      let res = await db.auth.get(1);
      return aes256.decrypt(encryptKey, res.refreshToken);
    } catch {
      return null;
    }
  }

  async getUser() {
    let res = await db.auth.get(1);
    if (res === undefined) {
      return null;
    }
    return res.user;
  }

  async getRememberIds() {
    let res = await db.auth.get(1);
    if (res === undefined) {
      return null;
    }
    return res.rememberIds;
  }

  async getAddons() {
    let res = await db.auth.get(1);
    if (res === undefined) {
      return null;
    }
    return res.addons;
  }

  // indexedDBに保存した有料・無料情報を取得
  async getIsFree() {
    let res = await db.auth.get(1);
    if (res === undefined) {
      return null;
    }
    return res.isFree;
  }

  async saveTokens(authResult) {
    return db.auth.put({
      id: 1,
      idToken: aes256.encrypt(encryptKey, authResult.idToken),
      refreshToken: aes256.encrypt(encryptKey, authResult.refreshToken),
      updated_at: new Date(),
    });
  }

  async saveAuthInfo(authResult) {
    return db.auth.put({
      id: 1,
      idToken: aes256.encrypt(encryptKey, authResult.idToken),
      refreshToken: aes256.encrypt(encryptKey, authResult.refreshToken),
      user: authResult.user,
      addons: authResult.addons,
      rememberIds: authResult.rememberIds,
      updated_at: new Date(),
      isFree: authResult.isFree,
    });
  }

  async saveUser(user) {
    db.auth.update(1, {
      user: user,
    });
  }

  async updateTokens(authResult) {
    db.auth.update(1, {
      idToken: aes256.encrypt(encryptKey, authResult.idToken),
      refreshToken: aes256.encrypt(encryptKey, authResult.refreshToken),
      updated_at: new Date(),
    });
  }

  async renewToken(force) {
    let result = await this.isExpiryToken();
    if (result || force) {
      let refreshToken = await this.getRefreshToken();
      const params = new URLSearchParams();
      params.append("grant_type", "refresh_token");
      params.append("refresh_token", refreshToken);
      let res;
      try {
        res = await http.post(
          "https://securetoken.googleapis.com/v1/token?key=" +
            firebaseConfig.apiKey,
          params
        );
      } catch (error) {
        // 保存している refresh token が無効（別タブ/別端末ログインによる revoke や
        // ローテーションで失効）でも、Firebase セッションが生きていれば
        // currentUser から取り直して復帰する。ここでは強制ログアウトしない。
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) return false;
        try {
          const idToken = await currentUser.getIdToken(true);
          const refreshToken = currentUser.refreshToken;
          await this.updateTokens({ idToken, refreshToken });
          Store.commit("SET_ID_TOKEN", { idToken });
          return true;
        } catch {
          return false;
        }
      }
      let authResult = {
        idToken: res.data.access_token,
        refreshToken: res.data.refresh_token,
      };
      await this.updateTokens(authResult);
      Store.commit("SET_ID_TOKEN", { idToken: res.data.access_token });
      return true;
    } else {
      return true;
    }
  }

  async saveAddons(addons) {
    db.auth.update(1, {
      addons: addons,
    });
  }

  async hasAddon(addonId) {
    let res = await db.auth.get(1);
    let found = res.addons.find(v => v.id === addonId);
    return typeof found !== "undefined";
  }

  async getVesselPositionCollectionName() {
    const hasForOhminato = await this.hasAddon(this.ADDON_IDS.FOR_OHMINATO);
    return hasForOhminato ? "vessel_position_for_ohminato" : "vessel_position";
  }

  async getMfa() {
    let res = await db.mfa.get(1);
    if (res === undefined) {
      return null;
    }
    return res;
  }

  async saveMfa(mfa) {
    db.mfa.put({
      id: 1,
      ...mfa,
    });
  }

  async clearMfa() {
    db.mfa.clear();
  }

  async getDeviceUuid(authInput) {
    if (!authInput) return null;
    const rawId = `${authInput.enterpriseId}_${authInput.userId}`;
    const hashedId = CryptoJS.SHA256(rawId).toString(CryptoJS.enc.Hex);
    const res = await db.device_uuid.get(hashedId);
    return res ? res.uuid : null;
  }

  async saveDeviceUuid(authResult) {
    const rawId = `${authResult.enterpriseId}_${authResult.userId}`;
    const hashedId = CryptoJS.SHA256(rawId).toString(CryptoJS.enc.Hex);
    await db.device_uuid.put({
      id: hashedId,
      uuid: authResult.uuid,
    });
  }

  // ログアウトしてもindexedDBに保存が必要なデータを取得
  async getPersistentData() {
    try {
      const rememberIds = await this.getRememberIds();
      const persistentData = rememberIds ? { rememberIds } : null;
      return persistentData;
    } catch {
      return null;
    }
  }

  // ログアウトしてもindexedDBに保存が必要なデータ以外を削除
  async clearNonPersistentData() {
    try {
      const persistentData = await this.getPersistentData();
      if (persistentData) {
        await db.auth.put({ id: 1, ...persistentData });
      } else {
        db.auth.clear(); // 保存が必要なデータがない場合はclear
      }
      return true;
    } catch {
      return false;
    }
  }

  async deleteDb() {
    await db.delete();
  }
}

export default new AuthService();
